import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const CONSENT_LABEL = "Yes — email me about tastings, offers and new arrivals. You can change this any time.";

/** Opt-in tick-box for the sign-up forms (unticked by default, as UK rules require). */
export function MarketingTick({ checked, onChange, testId }: { checked: boolean; onChange: (v: boolean) => void; testId: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 text-sm text-neutral-700">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-[var(--wine)]" data-testid={testId} />
      <span>{CONSENT_LABEL}</span>
    </label>
  );
}

/** One-time question on the member home for accounts that were never asked (joined before the tick-box existed). */
export function MarketingPrompt() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data } = useQuery<{ marketing?: { asked: boolean; status: string } }>({ queryKey: ["me-marketing"], queryFn: () => api.get("/auth/me").then((r) => r.data), enabled: !!user && user.role !== "admin", staleTime: 300000 });
  if (!user || user.role === "admin" || !data?.marketing || data.marketing.asked) return null;
  const answer = async (subscribed: boolean) => {
    setBusy(true);
    try {
      await api.put("/auth/me/marketing", { subscribed });
      toast.success(subscribed ? "Lovely — we'll keep you posted." : "Noted — no marketing emails.");
      qc.invalidateQueries({ queryKey: ["me-marketing"] });
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <section className="flex flex-wrap items-center gap-4 border border-border bg-[var(--paper)] px-5 py-4" data-testid="marketing-prompt">
      <Mail className="h-5 w-5 text-[var(--wine)]" strokeWidth={1.5} />
      <p className="flex-1 text-sm">Would you like our emails — tastings, offers and new arrivals? A few a month, never more.</p>
      <div className="flex gap-2">
        <button onClick={() => answer(true)} disabled={busy} className="bg-[var(--wine)] px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-[var(--paper)] disabled:opacity-50" data-testid="marketing-prompt-yes">Yes please</button>
        <button onClick={() => answer(false)} disabled={busy} className="border border-border px-4 py-2 text-xs uppercase tracking-[0.15em] disabled:opacity-50" data-testid="marketing-prompt-no">No thanks</button>
      </div>
    </section>
  );
}
